<template>
  <main class="relative overflow-hidden bg-[#0A1A38] min-h-screen pt-32 pb-40">
    <!-- Unified Background Atmospheric Radials -->
    <div class="absolute inset-0 z-0">
        <!-- Top Right Cyan Glow -->
      <div
          class="absolute -top-[5%] -right-[15%] w-[500px] h-[500px] bg-[#2AD1EE] rounded-full blur-[350px] pointer-events-none"
        ></div>

      <!-- Top Left Cyan Glow -->
      <div
          class="absolute top-[25%] -left-[15%] w-[500px] h-[500px] bg-[#2AD1EE] rounded-full blur-[350px] pointer-events-none"
        ></div>

     <!-- Bottom Left Cyan Glow -->
        <div
          class="absolute bottom-[5%] -right-[15%] w-[500px] h-[500px] bg-[#2AD1EE] rounded-full blur-[350px] pointer-events-none"
        ></div>
    </div>

    <div class="relative z-10 container mx-auto px-6 max-w-[1400px]">
      <div
        v-motion
        :initial="{ opacity: 0, y: 30 }"
        :enter="{
          opacity: 1,
          y: 0,
          transition: { duration: 800, ease: 'easeOut' },
        }"
        class="text-center mb-16"
      >
        <h1 class="text-5xl md:text-7xl font-black font-bro-mega text-white uppercase tracking-tight leading-tight">
          All <span class="bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] bg-clip-text text-transparent">Projects</span>
        </h1>
        <p class="mt-6 text-[#C9C9C9] text-base md:text-lg font-medium max-w-2xl mx-auto leading-relaxed">
          Exploring our complete collection of streamer platforms and custom website solutions.
        </p>
      </div>

      <!-- Phase Switches -->
      <div
        v-motion
        :initial="{ opacity: 0, y: 15 }"
        :enter="{
          opacity: 1,
          y: 0,
          transition: { duration: 800, ease: 'easeOut', delay: 200 },
        }"
        class="flex justify-center items-center gap-4 mb-20"
      >
        <button
          @click="activePhase = 1"
          class="relative px-10 py-3 group overflow-visible focus:outline-none"
        >
          <div
            class="absolute inset-0 transform -skew-x-[20deg] transition-all duration-300"
            :class="
              activePhase === 1
                ? 'bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE]'
                : 'border border-[#2AD1EE]/30 bg-[#0A1A38]'
            "
          ></div>
          <span
            class="relative z-10 font-bro-mega font-black text-lg tracking-tighter uppercase transition-colors"
            :class="
              activePhase === 1
                ? 'text-white'
                : 'text-[#2AD1EE]/70 group-hover:text-[#2AD1EE]'
            "
          >
            Phase 1
          </span>
        </button>

        <button
          @click="activePhase = 2"
          class="relative px-10 py-3 group overflow-visible focus:outline-none"
        >
          <div
            class="absolute inset-0 transform -skew-x-[20deg] transition-all duration-300"
            :class="
              activePhase === 2
                ? 'bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE]'
                : 'border border-[#2AD1EE]/30 bg-[#0A1A38]'
            "
          ></div>
          <span
            class="relative z-10 font-bro-mega font-black text-lg tracking-tighter uppercase transition-colors"
            :class="
              activePhase === 2
                ? 'text-white'
                : 'text-[#2AD1EE]/70 group-hover:text-[#2AD1EE]'
            "
          >
            Phase 2
          </span>
        </button>
      </div>

      <!-- Projects Grid -->
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 min-h-[600px]"
        style="perspective: 2000px"
      >
        <div
          v-for="(project, index) in filteredProjects"
          :key="project.id"
          v-motion
          :initial="{
            opacity: 0,
            x: -200,
            rotateZ: -15,
            scale: 0.8,
            filter: 'blur(5px)',
          }"
          :enter="{
            opacity: 1,
            x: 0,
            rotateZ: 0,
            scale: 1,
            filter: 'blur(0px)',
            transition: {
              type: 'spring',
              stiffness: 140,
              damping: 14,
              mass: 1,
              delay: index * 100,
            },
          }"
          class="flex flex-col bg-[#0A1A38]/90 backdrop-blur-xl border border-white/10 rounded-[2rem] overflow-hidden group hover:border-[#2AD1EE]/40 hover:scale-[1.02] hover:shadow-[0_0_60px_rgba(42,209,238,0.2)] transition-all duration-500 ease-out cursor-default relative"
        >
          <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
            <div class="absolute inset-0 border-[2px] border-[#2AD1EE]/20 rounded-[2rem]"></div>
            <div class="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-[#2AD1EE] rounded-tl-[2rem]"></div>
            <div class="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-[#2AD1EE] rounded-br-[2rem]"></div>
          </div>

          <div class="p-5 pb-0">
            <div class="relative aspect-video rounded-3xl overflow-hidden border border-white/5 shadow-inner">
              <img
                :src="require(`@/assets/projects/${project.image}`)"
                :alt="project.name"
                class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                loading="lazy"
              />
              <div class="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0A1A38]/90 to-transparent"></div>
            </div>
          </div>

          <div class="relative p-8 pt-6 text-center flex-grow flex flex-col justify-between">
            <div class="relative z-10 mb-8">
              <h3 class="text-2xl md:text-3xl font-black text-white uppercase tracking-wider mb-2 font-bro-mega group-hover:text-[#2AD1EE] transition-colors duration-300">
                {{ project.name }}
              </h3>
              <p class="text-[#C9C9C9]/60 text-xs md:text-sm uppercase tracking-[0.2em] font-medium leading-relaxed">
                Phase {{ activePhase }} Project - Fully Customized<br />Website Design
              </p>
            </div>

            <a
              :href="project.link"
              target="_blank"
              rel="noopener noreferrer"
              class="relative z-20 group/btn block mx-4 mt-auto transition-transform duration-300 active:scale-95"
            >
              <div class="skew-x-[-20deg] border border-[#2AD1EE]/30 bg-white/5 py-4 transition-all duration-300 group-hover/btn:bg-[#2AD1EE] group-hover/btn:border-[#2AD1EE] relative overflow-hidden shadow-[0_0_20px_rgba(42,209,238,0)] group-hover/btn:shadow-[0_0_30px_rgba(42,209,238,0.3)]">
                <div class="absolute inset-0 -translate-x-full group-hover/btn:animate-shine-fast pointer-events-none bg-gradient-to-r from-transparent via-white/70 to-transparent z-0"></div>
                <span class="skew-x-[20deg] block font-black text-[#2AD1EE] group-hover/btn:text-[#0A1A38] text-xs sm:text-sm uppercase tracking-[0.3em] transition-colors duration-300 relative z-10">
                  Visit Site
                </span>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Back to Top / CTA -->
      <div class="mt-20 text-center">
        <router-link to="/" class="relative inline-flex items-center justify-center px-12 py-3 group overflow-visible">
          <div class="absolute inset-0 border border-[#2AD1EE]/30 bg-[#04022E]/40 transform -skew-x-[20deg] transition-all duration-300 group-hover:bg-[#2AD1EE]/10 group-hover:border-[#2AD1EE]"></div>
          <span class="relative z-10 font-bro-mega font-black text-[#2AD1EE] text-lg tracking-tighter uppercase transition-colors duration-300 group-hover:text-cyan-300">
            Back to Home
          </span>
        </router-link>
      </div>
    </div>
  </main>
</template>

<script>
import { defineComponent } from "vue";
import { phase1Projects, phase2Projects } from "@/data/projects";

export default defineComponent({
  name: "ProjectsPage",
  data() {
    return {
      activePhase: 1,
      phase1Projects,
      phase2Projects,
    };
  },
  computed: {
    filteredProjects() {
      return this.activePhase === 1
        ? this.phase1Projects.filter((p) => p.display)
        : this.phase2Projects.filter((p) => p.display);
    },
  },
  mounted() {
    window.scrollTo(0, 0);
  }
});
</script>

<style scoped>
@keyframes shine-fast {
  0% { transform: translateX(-100%) skewX(-20deg); }
  100% { transform: translateX(200%) skewX(-20deg); }
}

.animate-shine-fast {
  animation: shine-fast 1s infinite;
}
</style>
